import * as React from 'react'
import { useQuery } from '@tanstack/react-query'
import { IssuingElements } from '../../../../externalServices/stripe/useLoadStripe'
import { AliceCardFragment } from '@alice-financial/alice-sdk'
import { useEphemeralKey } from './useEphemeralKey'

const useNonceQuery = (
  issuingElementsPromise: Promise<IssuingElements>,
  issuingCard: string,
  isBeta: boolean = false,
  enabled: boolean = true
) =>
  useQuery(
    ['nonce', issuingCard, isBeta ? 'beta' : 'standard'],
    () =>
      issuingElementsPromise
        .then(({ stripe }) => stripe.createEphemeralKeyNonce({ issuingCard }))
        .then((nonceResult) => nonceResult.nonce),
    {
      cacheTime: 0, // always get new nonce
      enabled, // only run query if enabled
    }
  )

// Return data required to instantiate Stripe elements for a particular card.
// `nonce` is provided by stripe.js, and passed to our backend to generate an ephemeral key.
// All of this is then passed along to stripe issuing elements to do things like securely
// fetch card info, and render the "add to apple wallet" button.
export const useIssuingElementsConfig = (
  card: AliceCardFragment,
  issuingElementsPromise: Promise<IssuingElements>,
  isBeta: boolean = false,
  enabled: boolean = true
) => {
  if (!card.bankingSystemId) throw new Error('Card is not an issuing card')
  const issuingCard = card.bankingSystemId
  const { data: nonce } = useNonceQuery(issuingElementsPromise, issuingCard, isBeta, enabled)
  const { data: ephemeralKey } = useEphemeralKey(
    { cardId: card.id, nonce: nonce || '' },
    { enabled: enabled && Boolean(nonce) }
  )
  const ephemeralKeySecret = ephemeralKey?.secret

  return React.useMemo(
    () => ephemeralKeySecret && { issuingCard, ephemeralKeySecret, nonce },
    [issuingCard, ephemeralKeySecret, nonce]
  )
}
